/**
 * Event types produced by our sync logic
 * Some events map directly to HTMLVideoElement events
 * Some events are custom events that we use to track the sync state
 *
 * Playback
 * - PLAY: when the video starts playing
 * - PAUSE: when the video is paused
 * Timing
 * - SEEKING: when the user starts controlling the scrubber
 * - SEEKED: when the video has seeked to a new time
 * - TIME_UPDATE: when the video's current time changes
 * Playback Rate
 * - RATE_CHANGE: when the video's playback rate changes
 * Leader (internal, only pay attention to timing events from the leader)
 * - LEADER: when the leader changes, either by hover or "election"
 *
 */
export enum SyncEventType {
  PLAY = 'play',
  PAUSE = 'pause',
  SEEKING = 'seeking',
  SEEKED = 'seeked',
  TIME_UPDATE = 'timeupdate',
  RATE_CHANGE = 'ratechange',
  LEADER = 'leader',
  ENDED = 'ended',
  SYNC = 'sync',
}

// Type aliases for context partials that events can update
export type PlayEventData = {
  isPlaying: true;
  currentTime?: number;
  playbackRate?: number;
};

export type PauseEventData = {
  isPlaying: false;
  currentTime?: number;
  playbackRate?: number;
  isEndOfVideo?: boolean;
};

export type SeekingEventData = {
  isPlaying: false;
  currentTime: number;
  playbackRate?: number;
};

export type SeekedEventData = {
  currentTime: number;
  isPlaying?: boolean;
  playbackRate?: number;
};

export type LeaderEventData = {
  videoId: string;
  currentTime?: number;
  isPlaying?: boolean;
  playbackRate?: number;
};

export type TimeUpdateEventData = {
  currentTime: number;
  playbackRate?: number;
};

export type RateChangeEventData = {
  playbackRate: number;
  currentTime?: number;
  isPlaying?: boolean;
};

export type EndedEventData = {
  currentTime: number;
  isPlaying?: boolean;
  playbackRate?: number;
};

export type ApplySyncEventData = {};

// Generic event type helper
type SyncEvent<T extends SyncEventType, D> = {
  type: T;
  data: D;
};

// Discriminated union for event data based on event type
export type SyncEventData =
  | SyncEvent<SyncEventType.PLAY, PlayEventData>
  | SyncEvent<SyncEventType.PAUSE, PauseEventData>
  | SyncEvent<SyncEventType.SEEKING, SeekingEventData>
  | SyncEvent<SyncEventType.SEEKED, SeekedEventData>
  | SyncEvent<SyncEventType.TIME_UPDATE, TimeUpdateEventData>
  | SyncEvent<SyncEventType.RATE_CHANGE, RateChangeEventData>
  | SyncEvent<SyncEventType.LEADER, LeaderEventData>
  | SyncEvent<SyncEventType.ENDED, EndedEventData>
  | SyncEvent<SyncEventType.SYNC, ApplySyncEventData>;

// Helper type to extract data from event based on type
export type EventData<T extends SyncEventType> = Extract<
  SyncEventData,
  {type: T}
>['data'];

export type PublishEventData = SyncEventData & {
  timestamp: number;
  videoId?: string;
  correlationId?: string;
};
